/** Dashboard credential storage and authenticated fetch with read retries. */
import {
  dashboardTokenStorageKey,
  dashboardOperatorStorageKey,
  dashboardRoleStorageKey
} from "./dashboard-urls";

export class DashboardAuthRequiredError extends Error {}
export const dashboardReadFetchAttempts = 4;
export const dashboardReadFetchTimeoutMs = 60000;

export function getDashboardToken(): string {
  return window.localStorage.getItem(dashboardTokenStorageKey)?.trim() ?? "";
}

export function getDashboardOperator(): string {
  return window.localStorage.getItem(dashboardOperatorStorageKey)?.trim() ?? "";
}

export function getDashboardRole(): string {
  return window.localStorage.getItem(dashboardRoleStorageKey)?.trim() ?? "";
}

export function hasDashboardCredentials(): boolean {
  return Boolean(getDashboardToken());
}

export async function authenticatedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getDashboardToken();
  if (token && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${token}`);
  }
  const operator = getDashboardOperator();
  const role = getDashboardRole();
  if (!headers.has("x-dashboard-operator")) {
    headers.set("x-dashboard-operator", operator || "admin");
  }
  if (!headers.has("x-dashboard-role")) {
    headers.set("x-dashboard-role", role || "admin");
  }
  const isReadRequest = isDashboardReadRequest(init);
  const attempts = isReadRequest ? dashboardReadFetchAttempts : 1;
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const controller = isReadRequest && !init.signal ? new AbortController() : null;
    const timer = controller
      ? window.setTimeout(() => controller.abort(new Error(`Dashboard read timed out after ${dashboardReadFetchTimeoutMs}ms`)), dashboardReadFetchTimeoutMs)
      : null;
    try {
      const requestInit: RequestInit = { ...init, headers };
      const signal = controller?.signal ?? init.signal;
      if (signal) requestInit.signal = signal;
      if (isReadRequest && requestInit.cache == null) requestInit.cache = "no-store";
      const response = await fetch(input, requestInit);
      if (response.status === 401) throw new DashboardAuthRequiredError("Dashboard access token required.");
      if (response.status >= 500 && attempt < attempts) {
        await response.text().catch(() => "");
        await dashboardReadRetryDelay(attempt);
        continue;
      }
      return response;
    } catch (error) {
      if (error instanceof DashboardAuthRequiredError) throw error;
      lastError = error;
      if (attempt >= attempts) break;
      await dashboardReadRetryDelay(attempt);
    } finally {
      if (timer !== null) window.clearTimeout(timer);
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Dashboard request failed.");
}

export function isDashboardReadRequest(init: RequestInit): boolean {
  const method = String(init.method ?? "GET").toUpperCase();
  return method === "GET" || method === "HEAD";
}

export function dashboardReadRetryDelay(attempt: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, Math.min(1000 * attempt, 3000)));
}
